<script setup lang="ts">
    import { computed, type Ref, ref } from 'vue';
    import {
        type DuplicateStudyOperation,
        type StudyProcessOptionsRepresentation,
        useCaseAdminStore,
    } from '@/views/admin/adminStore';
    import { logger } from '@/util';
    import type { ApiStudy } from '@/api/study/study';

    const props = defineProps<{ study: ApiStudy; operation: DuplicateStudyOperation }>();
    const log = logger();
    const store = useCaseAdminStore();

    const state: Ref<'hidden' | 'showing' | 'working' | 'error' | 'complete'> = ref('hidden');
    const _showCreateButton = computed(() => ['showing', 'working', 'error'].includes(state.value));

    const _initialOptions = (): StudyProcessOptionsRepresentation => ({
        send_email: false,
        create_notifications: true,
    });
    const _options = ref<StudyProcessOptionsRepresentation>(_initialOptions());

    const isVisible = computed({
        get: () => {
            return state.value !== 'hidden';
        },
        set: (value: boolean) => {
            if (value) {
                _options.value = _initialOptions();
                state.value = 'showing';
            } else {
                state.value = 'hidden';
            }
        },
    });

    const onCreate = async () => {
        try {
            state.value = 'working';
            await store.duplicateStudy(props.study, props.operation, _options.value);
            state.value = 'complete';
        } catch (e) {
            log.error('Error while duplicating study: %s', e);
            state.value = 'error';
        }
    };
</script>

<template>
    <app-modal v-model="isVisible" width="600">
        <template #activator="{ props: activatorProps }">
            <v-btn
                height="30"
                color="primary"
                variant="outlined"
                density="compact"
                v-bind="activatorProps"
                rounded
                class="px-4"
            >
                <span v-if="operation === 'files'">Rerun from DICOMs</span>
                <span v-if="operation === 'models'">Rerun from models</span>
            </v-btn>
        </template>

        <v-card min-height="400px" class="d-flex-column fill-height">
            <app-modal-title class="headline" @close="isVisible = false">
                Rerun Study
            </app-modal-title>
            <app-modal-content v-if="state === 'complete'">
                <div class="d-flex ga-2 align-center">
                    <v-icon icon="mdi-check-circle" size="large" color="success" />
                    <p>Started rerun of study successfully</p>
                </div>
            </app-modal-content>
            <app-modal-content v-else-if="state === 'error'">
                <div class="d-flex ga-2 align-center">
                    <v-icon icon="mdi-alert-circle" size="large" color="error" />
                    <p>Error while attempting to rerun study - see logs</p>
                </div>
            </app-modal-content>
            <app-modal-content v-else>
                <span v-if="operation === 'files'">
                    Recreate the study using the same specification and original DICOM files.
                </span>
                <span v-if="operation === 'models'">
                    Create a new study using the same specification, reusing the segmented models
                    that have already been created.
                </span>
                <v-checkbox
                    v-model="_options.send_email"
                    hide-details
                    dense
                    :disabled="state !== 'showing'"
                    label="Send email"
                />
                <v-checkbox
                    v-model="_options.create_notifications"
                    hide-details
                    dense
                    :disabled="state !== 'showing'"
                    label="Create notifications"
                />
            </app-modal-content>
            <v-spacer />
            <app-modal-content class="align-self-end">
                <app-button-primary
                    v-if="_showCreateButton"
                    :loading="state === 'working'"
                    @click="onCreate"
                >
                    Create
                </app-button-primary>
            </app-modal-content>
        </v-card>
    </app-modal>
</template>

<style scoped lang="scss">
    a {
        color: rgb(var(--v-theme-primary));
    }
</style>
